import { useCallback, useContext, useEffect, useMemo } from "react";
import { GetMarkets200Response, InstrumentTypeResponse } from "../../codegen-api";
import {
  getDefaultInstrumentType,
  isInstrumentTypeEnabled,
} from "../../config/features";
import { useGetMarkets } from "./useGetMarkets";
import { MarketContext } from "../MarketContext";
import { getContractPriceStep } from "../../utils/instruments";
import { AssetResponse } from "../../utils/asset";
import { getDefaultTradingMarket } from "../../utils/defaultMarket";

export function useActiveMarketData() {
  const { market, setMarket } = useContext(MarketContext);

  // Market METADATA is fetched for every instrument type, including disabled
  // verticals. Disabling a vertical hides its entry points but deliberately
  // keeps the rows a user already holds visible (positions, orders, history),
  // and those rows need their market's price/amount steps to render correct
  // numbers — `getMarketPrecision` would otherwise fall back to a generic 0.01
  // step and display wrong sizes. Gating belongs on what the user can DO, not
  // on the reference data behind what they can still see.
  //
  // The Rules of Hooks forbid looping these over the registry, but the
  // `marketsByInstrument` map below is typed as a full Record, so a new
  // instrument type cannot be added without wiring a fetch for it here.
  const { activeMarkets: activeOptionMarkets } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Option
  );
  const { activeMarkets: activePerpMarkets } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Perpetual
  );
  const { activeMarkets: activeSpotMarkets } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Spot
  );

  const fallbackMarket = useMemo(() => getDefaultTradingMarket(), []);

  const marketsByInstrument: Record<
    InstrumentTypeResponse,
    GetMarkets200Response[] | undefined
  > = useMemo(
    () => ({
      [InstrumentTypeResponse.Option]: activeOptionMarkets,
      [InstrumentTypeResponse.Perpetual]: activePerpMarkets,
      [InstrumentTypeResponse.Spot]: activeSpotMarkets,
    }),
    [activeOptionMarkets, activePerpMarkets, activeSpotMarkets]
  );

  // No implicit "else perps" arm: an unknown derivative has no markets, and
  // callers treat `undefined` as "not loaded / not available". A DISABLED
  // vertical still resolves here — see the fetch comment above; selection is
  // gated by the route, the picker and the effect below, not by hiding data.
  const getMarketsForDerivative = useCallback(
    (derivative?: InstrumentTypeResponse) =>
      derivative ? marketsByInstrument[derivative] : undefined,
    [marketsByInstrument]
  );

  const marketExists = useCallback(
    ({
      asset,
      derivative,
    }: {
      asset?: AssetResponse;
      derivative?: InstrumentTypeResponse;
    }) => {
      const relevantMarkets = getMarketsForDerivative(derivative);

      return Boolean(
        asset &&
          derivative &&
          relevantMarkets?.some(
            (m) =>
              m.underlying_asset === asset &&
              m.instrument_type === derivative
          )
      );
    },
    [getMarketsForDerivative]
  );

  const marketsData = useMemo(
    () =>
      getMarketsForDerivative(market.derivative)?.filter(
        (m) => m.underlying_asset === market.asset
      ),
    [getMarketsForDerivative, market]
  );

  useEffect(() => {
    // The trading page itself is never shown for a switched-off vertical, so a
    // market that lands on one (stale URL, a row click that slipped through) is
    // sent back to the default rather than rendering a vertical this build does
    // not offer.
    if (!isInstrumentTypeEnabled(market.derivative)) {
      setMarket(fallbackMarket);
      return;
    }

    const currentMarkets = getMarketsForDerivative(market.derivative);
    const fallbackMarkets = getMarketsForDerivative(fallbackMarket.derivative);

    if (
      currentMarkets === undefined ||
      fallbackMarkets === undefined ||
      marketExists(market)
    ) {
      return;
    }

    const defaultDerivative = fallbackMarket.derivative;
    const firstDefaultMarket = getMarketsForDerivative(defaultDerivative)?.[0];
    setMarket(
      marketExists(fallbackMarket) || !firstDefaultMarket
        ? fallbackMarket
        : {
            asset: firstDefaultMarket.underlying_asset as AssetResponse,
            derivative: defaultDerivative,
          }
    );
  }, [
    fallbackMarket,
    getMarketsForDerivative,
    market,
    marketExists,
    setMarket,
  ]);

  const expiries = useMemo(() => {
    if (marketsData?.length) {
      // Expiry is in nano, so we strip off the last 9 digits
      const allExpiries = marketsData
        .map((v) => Number(v.expiry))
        .filter((v) => !!v);
      return Array.from(new Set(allExpiries));
    }
    return [];
  }, [marketsData]);

  const getMarketPrecision = useCallback(
    (
      asset?: AssetResponse,
      instrumentType: InstrumentTypeResponse = getDefaultInstrumentType()
    ) => {
      const m = marketsByInstrument[instrumentType]?.find(
        (v) => v.underlying_asset === asset
      );
      if (m) {
        return getContractPriceStep(m);
      }
      // Mirrors getContractPriceStep's no-market default, including min_order_amount.
      return {
        amount_step: 0.01,
        price_step: 0.01,
        amount_precision: 2,
        price_precision: 2,
        min_order_amount: 0.01,
      };
    },
    [marketsByInstrument]
  );

  return {
    market,
    setMarket,
    activeOptionMarkets,
    activePerpMarkets,
    activeSpotMarkets,
    fallbackMarket,
    marketsData,
    expiries,
    getMarketPrecision,
  };
}
